import type { NextRequest } from "next/server";

import {
  FOOTERLESS_ROUTES,
  LOCALE_PREFIX_REGEX,
  LOCALE_URL_PREFIXES,
  MINIMAL_SHELL_ROUTES,
  PROTECTED_ROUTES,
  TRANSPARENT_NAVBAR_PATHS,
} from "~/constants/routes.consts";
import { LOCALES_NAMESPACES } from "~/i18n/i18n.consts";
import { Locale, LocaleUrlPrefix } from "~/i18n/i18n.types";

import { INITIAL_LOCALE_COOKIE, PREFERRED_LOCALE_COOKIE } from "./constants";

/**
 * Returns the locale corresponding to a given URL prefix. Defaults to "en-US" if the prefix is not recognized.
 */
const getLocaleFromUrlPrefix = (prefix: LocaleUrlPrefix): Locale => {
  switch (prefix) {
    case "fr":
      return LOCALES_NAMESPACES.FR_FR;
    case "de":
      return LOCALES_NAMESPACES.DE_DE;
    case "es":
      return LOCALES_NAMESPACES.ES_ES;
    case "au":
      return LOCALES_NAMESPACES.EN_AU;
    case "ca":
      return LOCALES_NAMESPACES.EN_CA;
    case "uk":
      return LOCALES_NAMESPACES.EN_GB;
    default:
      return LOCALES_NAMESPACES.EN_US;
  }
};

/**
 * Returns the locale segment of a path (e.g. "de" from "/de/profile"),
 * or "" when the path is not locale-prefixed.
 */
const getLocalePrefixFromPath = (pathname: string): LocaleUrlPrefix => {
  const segmentMatch = pathname.match(LOCALE_PREFIX_REGEX)?.[0] || "";
  const urlPrefix = segmentMatch.slice(1) as LocaleUrlPrefix;
  return urlPrefix;
};

/**
 * Compiles a PROTECTED_ROUTES entry into a matcher: "*" stands in for exactly
 * one dynamic path segment, e.g. "/vendors/profile/*\/edit" matches
 * "/vendors/profile/123/edit" (and anything nested under it) but not
 * "/vendors/profile/123".
 */
const toRouteMatcher = (route: string): RegExp => {
  const pattern = route
    .split("/")
    .map((segment) =>
      segment === "*"
        ? "[^/]+"
        : segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    )
    .join("/");

  return new RegExp(`^${pattern}(?:/.*)?$`);
};

/** Builds a check for whether a route matcher matches `path`. */
const matchesPath = (path: string) => (matcher: RegExp) => matcher.test(path);

/** True when `pathname`, with any locale prefix stripped, matches one of `matchers`. */
const matchesAnyRoute = (pathname: string, matchers: RegExp[]): boolean =>
  matchers.some(matchesPath(pathname.replace(LOCALE_PREFIX_REGEX, "")));

const PROTECTED_ROUTE_MATCHERS = PROTECTED_ROUTES.map(toRouteMatcher);

/** True when `pathname` is, or is nested under, a protected route. */
export const isProtectedRoute = (pathname: string): boolean =>
  matchesAnyRoute(pathname, PROTECTED_ROUTE_MATCHERS);

const FOOTERLESS_ROUTE_MATCHERS = FOOTERLESS_ROUTES.map(toRouteMatcher);

/**
 * True when `pathname` should hide the site footer but keep the full navbar.
 *
 * @param {string} pathname - The current path; a locale prefix (ex. "/de") is optional.
 * @returns {boolean} Whether the path is in `FOOTERLESS_ROUTES`.
 */
export const isFooterlessRoute = (pathname: string): boolean =>
  matchesAnyRoute(pathname, FOOTERLESS_ROUTE_MATCHERS);

/**
 * True when `pathname` should render logo-only nav and no footer.
 */
export const isMinimalShellRoute = (pathname: string): boolean => {
  const normalized = pathname.replace(LOCALE_PREFIX_REGEX, "");
  return MINIMAL_SHELL_ROUTES.some(
    (route) =>
      normalized === route ||
      normalized.startsWith(`${route}/`) ||
      normalized.endsWith(route),
  );
};

/**
 * Every leading segment standing for "the locale", in both forms this app produces: the
 * public prefix ("/fr") and the internal `[locale]` segment ("/en-US") the next.config
 * rewrites still add for the routes left under `src/app/[locale]`. Accepting both means
 * the match holds whichever one `usePathname` reports.
 */
const LEADING_LOCALE_SEGMENT = new RegExp(
  `^/(?:${[...LOCALE_URL_PREFIXES, ...Object.values(LOCALES_NAMESPACES)].join("|")})(?=/|$)`,
  "i",
);

/** True when `pathname` is a route that opts into the transparent navbar. */
export const isTransparentNavbarRoute = (pathname: string | null): boolean => {
  if (!pathname) {
    return false;
  }

  const withoutLocale = pathname.replace(LEADING_LOCALE_SEGMENT, "");
  // "" (a bare locale segment) and "/fr/" both normalise to "/".
  const normalized = withoutLocale.replace(/\/+$/, "") || "/";

  return TRANSPARENT_NAVBAR_PATHS.has(normalized);
};

/**
 * Confirms that a string is a valid Locale
 */
const isValidLocale = (value: string | undefined): value is Locale => {
  return !!value && Object.values(LOCALES_NAMESPACES).includes(value as Locale);
};

/**
 * The precedence shared by every locale-from-cookies lookup: an explicit
 * preference first, then the locale the visitor landed on. Undefined when neither is set.
 * Exported for `routes.server.utils.ts`, which applies it to `cookies()` rather than to a
 * request, so the two readings cannot drift apart.
 */
const resolveLocale = (
  preferred: string | undefined,
  initial: string | undefined,
): Locale | undefined => {
  const cookieLocale = preferred ?? initial;
  return isValidLocale(cookieLocale) ? cookieLocale : undefined;
};

/**
 * The visitor's locale as recorded in cookies, using the same precedence as the client and the Statsig user. Undefined when neither cookie is set.
 */
const getLocaleFromRequestCookies = (
  request: NextRequest,
): Locale | undefined =>
  resolveLocale(
    request.cookies.get(PREFERRED_LOCALE_COOKIE)?.value,
    request.cookies.get(INITIAL_LOCALE_COOKIE)?.value,
  );

export {
  getLocaleFromRequestCookies,
  getLocaleFromUrlPrefix,
  getLocalePrefixFromPath,
  isValidLocale,
  resolveLocale,
};
